import React, { useContext, useEffect, useState } from 'react'
import { Table, Button, Typography, Affix, App, Modal, Form, Space, Switch, Image, Avatar } from 'antd'
import { isMobileOnly, isDesktop } from "react-device-detect"
import { useNavigate, Link } from "react-router-dom"
import _ from "lodash"
import dayjs, { Dayjs } from 'dayjs'
import { FaPlus, FaEdit, FaDownload, FaImage, FaTrash, FaSearch } from "react-icons/fa"
import { API, Enum, Font } from "../../../resources/constants"
import language from "../../../resources/languages/fa_IR"

// Contexts
import GlobalContext from "../../../contexts/global";
import AxiosContext from "../../../contexts/axios"

// Interfaces
import { AxiosError, AxiosResponsePaginate } from "../../../interfaces/axios"
import { AntTableParams } from "../../../interfaces/ant"
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import type { FilterValue, SorterResult } from 'antd/es/table/interface'

const currency = new Intl.NumberFormat()
const { Pages: { TicketManagement: { Priority: PriorityLanguage } }, Enum: EnumLanguage, Share, Message: MessageLanguage } = language
const {  TICKET_MANAGEMENT_PRIORITY_PAGINATE, TICKET_MANAGEMENT_PRIORITY_REMOVE } = API
const { Message, Payment, Component } = Enum
const { IRANSans, IRANSansFaNum } = Font

interface ProvinceType {
    id: string,
    name: string,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface CityType {
    id: string,
    name: string,
    province: ProvinceType,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface CompanyType {
    row: number,
    id: string,
    key: string,
    number: string,
    position: number,
    name: string,
    tel: string,
    cities: CityType[],
    publish: boolean,
    tickets?: { count: number, seat: number }[]
    seatOne?: number,
    seatTwo?: number,
    seatThree?: number,
    seatLeft?: number,
    carLeft?: number,
    countService?: number,
    priority?: number,
    createdAt: Date,
    updatedAt: Date,
}

interface UserType {
    id: string,
    firstName: string,
    lastName: string,
    nationalCode: string,
    mobile: string,
    email?: string,
    createdAt: Date,
    updatedAt: Date,
}

interface PriorityType {
    row: number,
    id: string,
    key: string,
    destination: CityType,
    company: CompanyType,
    seat: number,
    operator?: UserType,
    createdAt: Date,
    updatedAt: Date,
}

export default function Index() {

    const [formModal] = Form.useForm()
    const navigate = useNavigate()
    const { accessToken, baseURL } = useContext(GlobalContext)
    const [priorities, setPriorities] = useState<PriorityType[]>([])
    const [loadingRecords, setLoadingRecords] = useState<boolean>(false)
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        total: undefined,
        current: 1,
        pageSize: 10,
        showTotal: isMobileOnly ? undefined : (total: number): string => `${Share.paginationShowTotal} (${total})`,
        showSizeChanger: !isMobileOnly,
        showQuickJumper: !isMobileOnly,
    })
    const { axios } = useContext(AxiosContext)
    const { message, modal } = App.useApp()

    const priorityColumns: ColumnsType<PriorityType> = [
        {
            title: Share.row,
            dataIndex: "row",
            key: "row",
        },
        {
            title: PriorityLanguage.destination,
            dataIndex: "destination",
            render: (text, record) => text && `${text.name} (${text.province.name})`
        },
        {
            title: PriorityLanguage.company,
            dataIndex: "company",
            key: "company",
            render: (text: CompanyType, record) => text && text.name
        },
        {
            title: PriorityLanguage.seat,
            dataIndex: "seat",
            key: "seat",
        },
        {
            title: PriorityLanguage.operator,
            dataIndex: "operator",
            key: "operator",
            render: (text, record) => text && `${text.firstName} ${text.lastName}`,
        },
        {
            title: PriorityLanguage.createdAt,
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: true,
            render: (text, record) => text && dayjs(text).tz(process.env.REACT_APP_TZ).format('HH:mm YYYY/MM/DD')
        },
        {
            title: PriorityLanguage.updatedAt,
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            sorter: true,
            render: (text, record) => text && dayjs(text).tz(process.env.REACT_APP_TZ).format('HH:mm YYYY/MM/DD')
        },
        {
            title: Share.edit,
            dataIndex: "edit",
            key: "edit",
            render: (text, record) =>  <Link to={'/ticket-management/priority/edit/' + record.id}><FaEdit size={20} /></Link>
        },
        {
            title: Share.delete,
            dataIndex: "delete",
            key: "delete",
            render: (text, record) =>  (
                <Typography.Link onClick={() => modal.confirm({
                    title: Share.deleteConfirmTitle,
                    okText: Share.deleteConfirmOk,
                    okType: 'danger',
                    cancelText: Share.deleteConfirmCancel,
                    onOk() {
                        setLoadingRecords(true)
                        axios.delete(TICKET_MANAGEMENT_PRIORITY_REMOVE + record.id).then((response: {}) => {
                            const { total = 1, pageSize = 1, current = 1 } = pagination
                            const params = {
                                page: total % pageSize !== 1 ? current : current - 1,
                                limit: pageSize,
                            }
                            getPriorities(params)
                            message.success(MessageLanguage[Message.DELETE_RECORD]).then(response => response)
                        }).catch((error: AxiosError) => {
                            const { response: { data } } = error
                            // @ts-ignore
                            message.error(MessageLanguage[data.message]).then(response => response )
                            setLoadingRecords(false)
                        })
                    }
                })}><FaTrash size={20} /></Typography.Link>
            )
        }
    ]

    const getPriorities = (params: AntTableParams) => {
        setLoadingRecords(true)
        axios.post(TICKET_MANAGEMENT_PRIORITY_PAGINATE, {}, { params }).then((response: AxiosResponsePaginate) => {
            const { data: { docs: priorities, page: current, limit: pageSize, totalDocs: total } } = response
            let row = (current - 1) * pageSize
            setPriorities(priorities.map<PriorityType>((priority: PriorityType) => ({ ...priority, row: ++row, key: priority.id })))
            setLoadingRecords(false)
            setPagination({
                ...pagination,
                current,
                pageSize,
                total,
            })
        }).catch((error: AxiosError) => {
            const { response: { data } } = error
            // @ts-ignore
            message.error(MessageLanguage[data.message]).then(response => response )
            setLoadingRecords(false)
        })
    }

    useEffect(() => {
        const { current, pageSize } = pagination
        getPriorities({ page: current, limit: pageSize })
    }, [])

    const handleTableChange = (
        pagination: TablePaginationConfig,
        filtersTable: Record<string, FilterValue|null>,
        sorter: SorterResult<any>|SorterResult<any>[]
    ): void => {
        const { current, pageSize } = pagination
        const sort = !_.isArray(sorter) && sorter.order && `${sorter.field},${sorter.order && sorter.order.slice(0, -3)}`
        getPriorities({ page: current, limit: pageSize, sort })
    }

    return (
        <>
            <Table
                columns={priorityColumns}
                dataSource={priorities}
                pagination={pagination}
                loading={loadingRecords}
                onChange={handleTableChange}
                scroll={{ x: true }}
            />
            <Affix offsetBottom={30}>
                <Space.Compact block style={{marginTop:'10px'}}>
                    <Button
                        key='add'
                        type="primary"
                        icon={<FaPlus />}
                        style={{height:'40px'}}
                        onClick={() => navigate('/ticket-management/priority/add')}>
                        {PriorityLanguage.addPriority}
                    </Button>
                </Space.Compact>
            </Affix>
        </>
    )
}
